import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MdBusiness, MdSensors, MdDeveloperBoard, MdAdd, MdRefresh } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddCompanyModal from '../../components/admin/AddCompanyModal';
import { useLanguage } from '../../context/LanguageContext';

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.4 } })
};

export default function AdminDashboard() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const navigate = useNavigate();
    const { t } = useLanguage();

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await api.get('/companies');
            setCompanies(res.data.data || []);
        } catch {
            toast.error(t('companies.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const totalSystems = companies.reduce((sum, c) => sum + (c.systems?.length || 0), 0);
    const totalSensors = companies.reduce((sum, c) => {
        return sum + (c.systems || []).reduce((s2, sys) => s2 + (sys.sensors?.length || 0), 0);
    }, 0);

    return (
        <div className="page-wrapper">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">{t('adminDashboard.title')}</h1>
                    <p className="page-subtitle">{t('adminDashboard.subtitle')}</p>
                </div>
                <div className="page-header-actions">
                    <button className="btn btn-secondary btn-sm" onClick={fetchData} id="refresh-btn">
                        <MdRefresh /> {t('common.refresh')}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)} id="add-company-btn">
                        <MdAdd /> {t('companies.addCompany')}
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="stats-grid">
                <motion.div custom={0} variants={fadeUp} initial="hidden" animate="show" className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                        <MdBusiness />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{loading ? '—' : companies.length}</div>
                        <div className="stat-label">{t('adminDashboard.totalCompanies')}</div>
                    </div>
                </motion.div>

                <motion.div custom={1} variants={fadeUp} initial="hidden" animate="show" className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--success)' }}>
                        <MdDeveloperBoard />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{loading ? '—' : totalSystems}</div>
                        <div className="stat-label">{t('adminDashboard.totalSystems')}</div>
                    </div>
                </motion.div>

                <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(139,92,246,0.15)', color: 'var(--secondary)' }}>
                        <MdSensors />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{loading ? '—' : totalSensors}</div>
                        <div className="stat-label">{t('adminDashboard.totalSensors')}</div>
                    </div>
                </motion.div>
            </div>

            {/* Companies List */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card"
            >
                <div className="card-header">
                    <div className="card-title">
                        <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                            <MdBusiness />
                        </div>
                        {t('companies.allCompanies')}
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/companies')} id="all-companies-btn">
                        {t('adminDashboard.viewAll')}
                    </button>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-container"><div className="spinner"></div></div>
                    ) : companies.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">🏢</div>
                            <div className="empty-state-title">{t('companies.noCompanies')}</div>
                            <div className="empty-state-desc">{t('companies.clickToAdd')}</div>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{t('companies.companyName')}</th>
                                        <th>{t('companyDetail.address')}</th>
                                        <th>{t('common.system')}</th>
                                        <th>{t('common.sensor')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((company, i) => {
                                        const sensorCount = (company.systems || []).reduce((s, sys) => s + (sys.sensors?.length || 0), 0);
                                        return (
                                            <motion.tr
                                                key={company._id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.1 * i }}
                                            >
                                                <td>
                                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{company.name}</div>
                                                </td>
                                                <td>{company.address}</td>
                                                <td>
                                                    <span className="badge badge-primary">
                                                        <MdDeveloperBoard /> {company.systems?.length || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge badge-primary">
                                                        <MdSensors /> {sensorCount}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => navigate(`/admin/companies/${company._id}`)}
                                                        id={`company-detail-${company._id}`}
                                                    >
                                                        {t('common.detail')}
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </motion.div>

            {showAddModal && (
                <AddCompanyModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => { setShowAddModal(false); fetchData(); }}
                />
            )}
        </div>
    );
}
